import { Injectable, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AffordabilityRequest, IPropertyBtl, IUiMainIncome, IUiOtherMortgage } from '@clubhub/ignite/models/affordability';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormFieldType } from '@shared/components/formly/formly.config';
import { IDateInput } from '@shared/components/formly/models/date-input';
import { IInputOption } from '@shared/components/formly/models/input';
import { FormsValidators } from '@shared/components/forms/validators/forms.validators';
import {
  customInitialPeriodFilterName,
  forTermFilterName,
  initialPeriodRangeOptions,
} from '@shared/constants/filter-preference-options';
import { KeyCodes } from '@shared/constants/key-codes';
import { dateToDateInput, mapDateToUtc } from '@shared/helpers/date-helpers';
import {
  ApplicantCountry,
  BtlType,
  EmploymentStatus,
  LenderName,
  LendingTypeCode,
  MortgageLenders,
  MortgageType,
  ProductLength,
  ProductLengthExtended,
  ProductType,
  ProductTypeString,
  ProductsModelRequest,
  RepaymentMethod,
} from '@shared/models';
import { LendingType } from '@shared/models/lending-type';
import { FormatService } from '@shared/services/format.service';
import { LendingTypeService } from './lending-type.service';

@Injectable({
  providedIn: 'root',
})
export class IgniteHelperService {
  private readonly formatService = inject(FormatService);
  private readonly lendingTypeService = inject(LendingTypeService);

  public readonly initialPeriodRangeOptions = initialPeriodRangeOptions;
  public readonly forTermFilterName = forTermFilterName;
  public readonly customFilterName = customInitialPeriodFilterName;

  public get yesNoOptions() {
    return [
      { value: true, icon: 'check', label: 'Yes', srOnly: true },
      { value: false, icon: 'times', label: 'No', srOnly: true },
    ];
  }

  public get yearMonthValidator() {
    return {
      lengthOfTime: {
        expression: (control: UntypedFormControl) => {
          const { years, months } = control.value;
          return years > 0 || months > 0;
        },
        message: (_error: unknown, field: FormlyFieldConfig) => `${field.props?.label} must be greater than 0`,
      },
      monthsValidation: {
        expression: (control: UntypedFormControl) => {
          const { months } = control.value;
          return months < 12;
        },
        message: (_error: unknown, field: FormlyFieldConfig) => `${field.props?.label} should be a maximum of 11`,
      },
      max40YearsValidationMessage: {
        expression: (control: UntypedFormControl) => {
          const { years, months } = control.value;
          return this.isMortgageTermValid(years, months);
        },
        message: (_error: unknown, field: FormlyFieldConfig) => `${field.props?.label} has a maximum of 40 years`,
      },
    };
  }

  public get yearMonthValidatorOnTouched() {
    return {
      lengthOfTime: {
        expression: (control: UntypedFormControl) => {
          if (control.untouched || control.value.years === '' || control.value.years === null) {
            return true;
          }

          const { years, months } = control.value;
          return years > 0 || months > 0;
        },
        message: (error: unknown, field: { parent: { props: { text: string } } }) =>
          `${field.parent.props.text} must be greater than 0 or undefined`,
        errorPath: 'years',
      },
    };
  }

  public mapNumberToProductLength(initialPeriod: number): ProductLengthExtended | undefined {
    switch (initialPeriod) {
      case 1:
        return ProductLengthExtended.OneYear;
      case 2:
        return ProductLengthExtended.TwoYears;
      case 3:
        return ProductLengthExtended.ThreeYears;
      case 4:
        return ProductLengthExtended.FourYears;
      case 5:
        return ProductLengthExtended.FiveYears;
      case 6:
        return ProductLengthExtended.SixYears;
      case 7:
        return ProductLengthExtended.SevenYears;
      case 10:
        return ProductLengthExtended.TenYears;
      case 15:
        return ProductLengthExtended.FifteenYears;
    }
  }

  public mapProductLengthToNumber(productLengthExtended: ProductLengthExtended) {
    switch (productLengthExtended) {
      case ProductLengthExtended.OneYear:
        return 1;
      case ProductLengthExtended.TwoYears:
        return 2;
      case ProductLengthExtended.ThreeYears:
        return 3;
      case ProductLengthExtended.FourYears:
        return 4;
      case ProductLengthExtended.FiveYears:
        return 5;
      case ProductLengthExtended.SixYears:
        return 6;
      case ProductLengthExtended.SevenYears:
        return 7;
      case ProductLengthExtended.TenYears:
        return 10;
      case ProductLengthExtended.FifteenYears:
        return 15;
    }
  }

  public getInitialProductPeriodFilterName(initialPeriod: ProductLength | ProductLengthExtended | undefined): string {
    if (!initialPeriod) {
      return '';
    }

    switch (initialPeriod) {
      case ProductLengthExtended.OneYear:
        return '1 year';
      case ProductLengthExtended.TwoYears:
        return '2 years';
      case ProductLengthExtended.ThreeYears:
        return '3 years';
      case ProductLengthExtended.FourYears:
        return '4 years';
      case ProductLengthExtended.FiveYears:
        return '5 years';
      case ProductLengthExtended.SixYears:
        return '6 years';
      case ProductLengthExtended.SevenYears:
        return '7 years';
      case ProductLengthExtended.TenYears:
        return '10 years';
      case ProductLengthExtended.FifteenYears:
        return '15 years';
      case ProductLengthExtended.Custom:
        return this.customFilterName;
      case ProductLengthExtended.ForTerm:
        return this.forTermFilterName;
    }

    return '';
  }

  public productLengthExtendedOptions(): IInputOption[] {
    return [
      ...this.initialPeriodRangeOptions.map(r => {
        const productLengthExtended = this.mapNumberToProductLength(r);
        return {
          value: productLengthExtended,
          label: this.getInitialProductPeriodFilterName(productLengthExtended),
        };
      }),
      {
        value: ProductLengthExtended.ForTerm,
        label: this.forTermFilterName,
      },
      {
        value: ProductLengthExtended.Custom,
        label: this.customFilterName,
      },
    ];
  }

  public yearsAndMonthsFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'years',
        type: FormFieldType.Number,
        className: 'col',
        defaultValue: 0,
        props: {
          label: 'Years',
          min: 0,
          max: 40,
          required: false,
          groupedField: true,
          collapseMargin: true,
        },
      },
      {
        key: 'months',
        type: FormFieldType.Number,
        className: 'col',
        defaultValue: 0,
        props: {
          label: 'Months',
          min: 0,
          max: 11,
          required: false,
          groupedField: true,
          collapseMargin: true,
        },
      },
    ];
  }

  public get defaultSelectOption(): IInputOption {
    return { value: null, label: '', disabled: true };
  }

  public get defaultSelectOptionNotDisabled(): IInputOption {
    return { value: null, label: '' };
  }

  public get productLengthOptions(): IInputOption[] {
    return [
      {
        value: ProductLength.OneYear,
        label: '1 year',
      },
      {
        value: ProductLength.TwoYears,
        label: '2 years',
      },
      {
        value: ProductLength.ThreeYears,
        label: '3 years',
      },
      {
        value: ProductLength.FourYears,
        label: '4 years',
      },
      {
        value: ProductLength.FiveYears,
        label: '5 years',
      },
    ];
  }

  public get productLengthBtlOptions(): IInputOption[] {
    return [
      {
        value: ProductLength.TwoYears,
        label: '2 years',
      },
      {
        value: ProductLength.ThreeYears,
        label: '3 years',
      },
      {
        value: ProductLength.FiveYears,
        label: '5 years',
      },
    ];
  }

  public get rateTypesOptions(): IInputOption[] {
    return [
      {
        value: ProductTypeString.Fixed,
        label: ProductTypeString.Fixed,
      },
      {
        value: ProductTypeString.Tracker,
        label: ProductTypeString.Tracker,
      },
      {
        value: ProductTypeString.Variable,
        label: ProductTypeString.Variable,
      },
      {
        value: ProductTypeString.Discount,
        label: ProductTypeString.Discount,
      },
    ];
  }

  public get productBtlTypeOptions(): IInputOption[] {
    return [
      {
        value: BtlType.Standard,
        label: 'Standard',
      },
      {
        value: BtlType.HMO,
        label: 'HMO',
      },
      {
        value: BtlType.HolidayLet,
        label: 'Holiday let',
      },
      {
        value: BtlType.MUFB,
        label: 'MUFB',
      },
    ];
  }

  public get productBtlTypeOptionsWithDefaultSelect(): IInputOption[] {
    const defaultBtlTypeOption: IInputOption[] = [this.defaultSelectOption];
    return defaultBtlTypeOption.concat(this.productBtlTypeOptions);
  }

  public get countryResidentTypeOptions(): IInputOption[] {
    return [
      {
        value: ApplicantCountry.England,
        label: 'England',
      },
      {
        value: ApplicantCountry.NorthernIreland,
        label: 'Northern Ireland',
      },
      {
        value: ApplicantCountry.Scotland,
        label: 'Scotland',
      },
      {
        value: ApplicantCountry.Wales,
        label: 'Wales',
      },
      {
        value: ApplicantCountry.OutsideUk,
        label: 'Country outside of the UK',
      },
    ];
  }

  public isEmployed(employmentStatus: EmploymentStatus | null): boolean {
    return (
      employmentStatus === EmploymentStatus.Employed ||
      employmentStatus === EmploymentStatus.SelfEmployed ||
      employmentStatus === EmploymentStatus.DirectorOrShareholder
    );
  }

  public isUnemployed(employmentStatus: EmploymentStatus | null): boolean {
    return (
      employmentStatus === EmploymentStatus.Homemaker ||
      employmentStatus === EmploymentStatus.Retired ||
      employmentStatus === EmploymentStatus.Student ||
      employmentStatus === EmploymentStatus.Unemployed
    );
  }

  public isHomemakerStudentOrUnemployed(employmentStatus: EmploymentStatus | null): boolean {
    return (
      employmentStatus === EmploymentStatus.Homemaker ||
      employmentStatus === EmploymentStatus.Student ||
      employmentStatus === EmploymentStatus.Unemployed
    );
  }

  public buildSelectOptionsFromEnum(enumeration: Record<string, unknown>, convertToLowerCase = true): IInputOption[] {
    const propertyNames = this.formatService.getEnumPropertyNames(enumeration);
    return [
      ...propertyNames.map(prop => {
        let optionText = this.formatService.toSentenceCase(prop, convertToLowerCase);
        if (prop === 'RentalIncome') {
          optionText = 'Rental income (Mortgage Free)';
        }
        return {
          value: enumeration[prop],
          label: optionText,
        };
      }),
    ];
  }

  public buildOptionsFromEnum(enumeration: any, convertToLowerCase = true): any[] {
    const propertyNames = this.formatService.getEnumPropertyNames(enumeration);
    return propertyNames.map(prop => {
      let optionText = this.formatService.toSentenceCase(prop, convertToLowerCase);
      if (prop === 'RentalIncome') {
        optionText = 'Rental income (Mortgage Free)';
      }

      return {
        value: enumeration[prop],
        label: optionText,
      };
    });
  }

  public getEnumLabel(value: number, enumeration: unknown, convertToLowerCase = true) {
    const types = this.buildOptionsFromEnum(enumeration, convertToLowerCase);
    const enumValue = types.find(s => s.value === value);
    return enumValue?.label;
  }

  public hiddenInput(key: string, hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null) {
    return {
      key,
      type: 'input',
      props: {
        type: 'hidden',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => hideExpression?.(field.model, field),
      },
    };
  }

  public currencyInput(
    key: string,
    text: string,
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    validators: unknown[] = [],
    expressions: FormlyFieldConfig['expressions'] | null = null,
    extraValidators: FormlyFieldConfig['validators'] | null = null,
    className: string | null = null,
    props: Record<string, unknown> | null = null,
    defaultValue: number | null = null,
    displayZeroIfDefaultValueIsNull = true,
  ) {
    return this.numberInput(
      key,
      text,
      tooltip,
      hideExpression,
      validators,
      expressions,
      extraValidators,
      className,
      null,
      null,
      props,
      'currency',
      defaultValue,
      displayZeroIfDefaultValueIsNull,
    );
  }

  public numberInput(
    key: string,
    text: string,
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    validators: unknown[] | null = null,
    expressions: FormlyFieldConfig['expressions'] | null = null,
    extraValidators: FormlyFieldConfig['validators'] | null = {},
    className: string | null = null,
    addonLeft: unknown = null,
    addonRight: unknown = null,
    props: Record<string, unknown> | null = {},
    type: string | null = null,
    defaultValue: number | null = null,
    displayZeroIfDefaultValueIsNull = true,
  ) {
    const field = {
      key,
      type: type ?? 'input',
      defaultValue: displayZeroIfDefaultValueIsNull ? (defaultValue ?? '0') : defaultValue,
      className,
      props: {
        ...{
          text,
          type: 'number',
          tooltip,
          required: true,
          min: 0,
          focus: (formlyField: FormlyFieldConfig, event) => {
            const { value } = event.target;
            const valid = FormsValidators.greaterThanZero(value);
            if (!valid) {
              event.target.value = '';
            }
            formlyField.formControl?.markAsUntouched();
          },
          blur: (formlyField: FormlyFieldConfig, event) => {
            formlyField.formControl?.markAsTouched();
            const { value } = event.target;
            if (displayZeroIfDefaultValueIsNull) {
              const valid = FormsValidators.greaterThanZero(value);
              if (!valid) {
                event.target.value = 0;
                formlyField.formControl?.setValue(0);
              }
            } else if (!displayZeroIfDefaultValueIsNull && !value) {
              formlyField.formControl?.reset();
            }
          },
          keydown: (formlyField: FormlyFieldConfig, event) => {
            const { value } = event.target;
            if (!value && !displayZeroIfDefaultValueIsNull) {
              formlyField.formControl?.reset();
            }
            // formlyField.formControl.markAsTouched();
          },
          keyup: (formlyField: FormlyFieldConfig, event) => {
            const key = event.which ?? event.keyCode ?? 0;
            if (key === KeyCodes.Tab || key === KeyCodes.Shift) {
              return;
            }
            formlyField.formControl?.markAsTouched();
          },
        },
        ...props,
      },
      validators: {
        validation: validators ?? [FormsValidators.integer],
        ...extraValidators,
      },
      expressions: {
        ...expressions,
        hide: (field: FormlyFieldConfig) => {
          return hideExpression?.(field.model, field);
        },
      },
    } as FormlyFieldConfig;

    if (field.props && addonLeft) {
      field.props.addonLeft = {
        text: addonLeft,
      };
    }

    if (field.props && addonRight) {
      field.props.addonRight = {
        text: addonRight,
      };
    }

    return field;
  }

  public radioInput(
    key: string,
    text: string,
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    options: IInputOption[] | null = null,
    expressions: FormlyFieldConfig['expressions'] | null = null,
    defaultValue: unknown | null = null,
    className: string | null = null,
    props: Record<string, unknown> = {},
  ) {
    return {
      key,
      type: 'customRadio',
      defaultValue,
      className,
      props: {
        text,
        tooltip,
        options: options ?? this.yesNoOptions,
        required: true,
        ...props,
      },
      expressions: {
        ...expressions,
        hide: field => hideExpression?.(field.model, field),
      },
    } as FormlyFieldConfig;
  }

  public checkboxInput(
    key: string,
    text: string,
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    options: IInputOption[] | null = null,
    expressions: Record<string, string | ((field: FormlyFieldConfig) => unknown)> | null = null,
    defaultValue = '',
  ) {
    const defaultObj: Record<string, unknown> = {};
    defaultObj[defaultValue] = true;
    return {
      key,
      type: 'customCheckbox',
      props: {
        text,
        tooltip,
        options: options ?? this.yesNoOptions,
        required: true,
      },
      defaultValue: defaultObj,
      expressions: {
        ...expressions,
        hide: (field: FormlyFieldConfig) => hideExpression?.(field.model, field),
      },
    };
  }

  public selectInput(
    key: string,
    text: string,
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    options: IInputOption[] | null = null,
    expressions: FormlyFieldConfig['expressions'] | null = null,
    defaultValue: unknown | null = null,
    className: string | null = null,
    props = {},
  ) {
    return {
      key,
      type: 'select',
      className,
      defaultValue,
      props: {
        text,
        tooltip,
        options,
        required: true,
        ...props,
      },
      expressions: {
        ...expressions,
        hide: (field: FormlyFieldConfig) => hideExpression?.(field.model, field),
      },
    } as FormlyFieldConfig;
  }

  public postcodeLookup(
    key: string,
    text: string,
    submitText: string,
    props = {},
    tooltip: string | null = null,
    hideExpression: ((model: unknown, field: FormlyFieldConfig) => boolean) | null = null,
    options: IInputOption[] | null = null,
    expressions: FormlyFieldConfig['expressions'] | null = null,
    defaultValue: unknown | null = null,
    className: string | null = null,
  ) {
    return {
      key,
      type: 'postcodeInput',
      defaultValue,
      className,
      props: {
        text,
        submitText,
        tooltip,
        options: options ?? this.yesNoOptions,
        required: true,
        ...props,
      },
      expressions: {
        ...expressions,
        hide: field => hideExpression?.(field.model, field),
      },
      validators: {
        validation: [FormsValidators.postcode],
      },
    } as FormlyFieldConfig;
  }

  public calculateRepaymentAmount(loanAmount: number, interestOnlyAmount: number | undefined): number {
    if (loanAmount && interestOnlyAmount) {
      const value = loanAmount - (interestOnlyAmount ?? 0);
      return value > 0 ? value : 0;
    }
    return 0;
  }

  public getEnumValue(value: string): number {
    return +value.split(':')[1];
  }

  public isMortgageTermValid(years: number, months: number): boolean {
    return years + months / 12 <= 40;
  }

  public canPaymentsCoverMortgageBalance(group: UntypedFormGroup): boolean {
    const otherMortgage = group.getRawValue() as IUiOtherMortgage;
    if (+(otherMortgage?.monthlyMortgagePayments ?? 0) > 0) {
      const term: number = +(otherMortgage?.remainingTermOfLoan?.years ?? 0) * 12 + +(otherMortgage?.remainingTermOfLoan?.months ?? 0);
      const paymentAmount: number = term * +(otherMortgage.monthlyMortgagePayments ?? 0);
      return otherMortgage.repaymentMethod !== RepaymentMethod.CapitalAndInterest ||
      paymentAmount >= +(otherMortgage.totalMortgageBalance ?? 0);
    }
    return true;
  }

  public canPaymentsCoverMortgageOutstanding(group: UntypedFormGroup): boolean {
    const otherMortgageBtl = group.getRawValue() as IPropertyBtl;
    if (+(otherMortgageBtl.monthlyBTLMortgageRepayment ?? 0) > 0) {
      const term: number = +(otherMortgageBtl.mortgageTerm?.years ?? 0) * 12 + +(otherMortgageBtl.mortgageTerm?.months ?? 0);
      const paymentAmount: number = term * +(otherMortgageBtl.monthlyBTLMortgageRepayment ?? 0);
      return (
        otherMortgageBtl.repaymentMethod !== RepaymentMethod.CapitalAndInterest ||
        paymentAmount >= +(otherMortgageBtl.totalBTLMortgageBalanceOutstanding ?? 0)
      );
    }
    return true;
  }

  public salaryAndDividendsGreaterThanZero() {
    return {
      expression: (control: UntypedFormControl) => {
        const {
          grossSalaryForLatestPeriod,
          grossDividendsForLatestPeriod,
          netProfitBeforeDividendsForLatestPeriod,
          grossAnnualIncome,
          employmentStatus,
        } = control.value as IUiMainIncome;
        return (
          employmentStatus !== EmploymentStatus.DirectorOrShareholder ||
          +(grossSalaryForLatestPeriod ?? 0) +
            +(grossDividendsForLatestPeriod ?? 0) +
            +(netProfitBeforeDividendsForLatestPeriod ?? 0) +
            +(grossAnnualIncome ?? 0) >
            0
        );
      },
      message:
        'Either gross annual income, director\'s remuneration for latest period, gross dividends for latest period ' +
        'or net profit for latest period should be greater than 0',
    };
  }

  public getMortgageLendersList(lenders: LenderName[]) {
    const lendersViewModels = lenders.map(lender => ({ value: lender.lenderName, label: lender.lenderName }));

    lendersViewModels.push({
      label: 'Other (please specify)',
      value: MortgageLenders.Other,
    });

    return lendersViewModels;
  }

  public getCurrentMortgageLender(mortgageLender: string | undefined, selectedLender: { value: string } | undefined): string | null {
    if (!!mortgageLender) {
      return mortgageLender;
    }

    if (!selectedLender || (selectedLender.value as MortgageLenders) === MortgageLenders.Other) {
      return null;
    }

    return selectedLender.value;
  }

  public getDateOrNull(dateInput: IDateInput | undefined): Date | null {
    if (!dateInput) {
      return null;
    }

    if (!dateInput.day || !dateInput.month || !dateInput.year) {
      return null;
    }

    return mapDateToUtc(dateInput);
  }

  public getProductTransferInitialPeriodValidation() {
    return {
      expression: FormsValidators.expression(FormsValidators.productTransferDateValidation()),
      message: 'The selected date must be in the future',
      errorPath: 'productTransferInitialDate',
    };
  }

  public getBtlLendingType(): LendingType {
    return {
      code: LendingTypeCode.Btl,
    } as LendingType;
  }

  public getResLendingType(): LendingType {
    return {
      code: LendingTypeCode.Res,
    } as LendingType;
  }

  public dateToDateInput(date: string | Date | undefined | null): IDateInput | null {
    if (!date) {
      return null;
    }
    return dateToDateInput(date);
  }

  public generateEmptyApplicantAges(count: number, startFrom: number = 0) {
    return Array.from({ length: count }, (_, i) => i).map(index => {
      return {
        applicantNumber: startFrom + index + 1,
        dateOfBirth: null,
      } as any;
    });
  }

  public isPurchaseRightToBuy(mortgageType: MortgageType | undefined, productType: ProductType | undefined) {
    return productType === ProductType.RightToBuy && mortgageType === MortgageType.Purchase;
  }

  public isRemortgageRightToBuy(mortgageType: MortgageType, productType: ProductType | undefined) {
    return productType === ProductType.RightToBuy && mortgageType === MortgageType.Remortgage;
  }

  public isSharedOwnershipRequest(request: ProductsModelRequest | AffordabilityRequest) {
    return this.isSharedOwnership(request.productTypeExtended);
  }

  public isSharedOwnership(productType?: ProductType) {
    if (!productType || !this.lendingTypeService.isResLendingType) {
      return false;
    }

    return productType === ProductType.SharedOwnership;
  }
}
